import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import SEO from '../components/SEO';
import { localBusinessSchema, contactPageSchema, breadcrumbSchema } from '../utils/structuredData';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    dealershipType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic would go here
    alert('Thank you for your inquiry! We will contact you within 24 hours.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      dealershipType: '',
      message: ''
    });
  };

  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 768px) {
          .contact-hero-h1 { font-size: 32px !important; letter-spacing: 1px !important; }
          .contact-hero-sub { font-size: 15px !important; }
          .contact-hero-section { padding: 60px 0 !important; }
          .contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .contact-h2 { font-size: 26px !important; }
          .contact-method { flex-direction: row !important; align-items: flex-start !important; gap: 12px !important; }
          .contact-method-value { font-size: 15px !important; word-break: break-word !important; overflow-wrap: anywhere !important; }
          .contact-method-text { min-width: 0 !important; flex: 1 !important; overflow: hidden !important; }
          .contact-form-container {
            padding: 24px 16px !important;
            margin: 0 !important;
            border-radius: 12px !important;
            box-sizing: border-box !important;
            width: 100% !important;
          }
          .contact-response-h2 { font-size: 28px !important; }
          .contact-section-pad { padding: 50px 0 !important; }
          .contact-response-section { padding: 50px 0 !important; }
          .contact-section-inner { padding: 0 16px !important; }
        }
      `}</style>
      
      <SEO
        title="Contact Driven2WIN — Schedule Your Free Event Strategy Call"
        description="Ready to pack your lot and crush your sales goals? Call 904-638-7200 or fill out our form for a FREE event strategy consultation. We respond within 24 hours. Serving car, golf cart, motorcycle, ATV, and RV dealers nationwide."
        keywords="contact driven2win, free event strategy call, dealership event consultation, automotive marketing quote, event marketing contact"
        canonicalUrl="https://driven2win.com/contact"
        structuredData={[contactPageSchema, localBusinessSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Contact',url:'https://driven2win.com/contact'}])]}
      />
      
      {/* Hero */}
      <section style={styles.hero} className="contact-hero-section">
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="contact-hero-h1">
            LET'S <span style={{color: '#FFC300'}}>TALK</span>
          </h1>
          <p style={styles.heroSubheadline} className="contact-hero-sub">
            Ready to pack your lot and crush your sales goals? Get in touch for a FREE event strategy call!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section style={styles.contactSection} className="contact-section-pad">
        <div style={styles.container}>
          <div className="contact-grid" style={styles.contactGrid}>
            {/* Contact Info */}
            <div style={styles.contactInfo}>
              <h2 style={styles.contactH2} className="contact-h2">GET IN TOUCH</h2>
              <p style={styles.contactText}>
                Fill out the form or give us a call — we're here to answer your questions and help you plan an event that delivers MASSIVE results!
              </p>
              
              <div style={styles.contactMethods}>
                <div style={styles.contactMethod}>
                  <div style={styles.contactMethodIcon}>
                    <Phone size={32} color="#E31E24" />
                  </div>
                  <div className="contact-method-text">
                    <div style={styles.contactMethodLabel}>PHONE</div>
                    <div style={styles.contactMethodValue} className="contact-method-value">904-638-7200</div>
                    <div style={styles.contactMethodNote}>Monday - Friday, 9am - 6pm EST</div>
                  </div>
                </div>
                
                <div style={styles.contactMethod}>
                  <div style={styles.contactMethodIcon}>
                    <Mail size={32} color="#FF6B35" />
                  </div>
                  <div className="contact-method-text">
                    <div style={styles.contactMethodLabel}>EMAIL</div>
                    <div style={styles.contactMethodValue} className="contact-method-value">info@driven2win.com</div>
                    <div style={styles.contactMethodNote}>We respond within 24 hours</div>
                  </div>
                </div>
                
                <div style={styles.contactMethod}>
                  <div style={styles.contactMethodIcon}>
                    <MapPin size={32} color="#0080FF" />
                  </div>
                  <div className="contact-method-text">
                    <div style={styles.contactMethodLabel}>HEADQUARTERS</div>
                    <div style={styles.contactMethodValue} className="contact-method-value">
                      5285 Shad Road, Suite 1619<br />
                      Jacksonville, FL 32257
                    </div>
                    <div style={styles.contactMethodNote}>Serving all 50 states nationwide</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div id="contact-form" style={styles.formContainer} className="contact-form-container">
              <h2 style={styles.formH2}>REQUEST A FREE CONSULTATION</h2>
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={styles.input}
                    placeholder="John Smith"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={styles.input}
                    placeholder="john@dealership.com"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={styles.input}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Dealership / Company Name *</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    style={styles.input}
                    placeholder="ABC Motors"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Dealership Type *</label>
                  <select
                    name="dealershipType"
                    value={formData.dealershipType}
                    onChange={handleChange}
                    required
                    style={styles.select}
                  >
                    <option value="">Select dealership type...</option>
                    <option value="car-truck">Car & Truck Dealership</option>
                    <option value="golf-cart">Golf Cart Dealership</option>
                    <option value="motorcycle">Motorcycle Dealership</option>
                    <option value="atv">ATV Dealership</option>
                    <option value="rv">RV Dealership</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tell Us About Your Goals</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    style={styles.textarea}
                    placeholder="Tell us about your event goals, timeline, budget, or any questions you have..."
                  />
                </div>
                
                <button type="submit" style={styles.submitBtn}>
                  <Send size={20} style={{marginRight: '12px'}} />
                  Send My Request
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Response Time */}
      <section style={styles.responseSection}>
        <div style={styles.container}>
          <h2 style={styles.responseH2}>
            <span style={{color: '#FFC300'}}>FAST RESPONSE</span> GUARANTEED
          </h2>
          <p style={styles.responseText}>
            We respond to all inquiries within 24 hours. For urgent requests, call us directly at <strong>904-638-7200</strong> during business hours for immediate assistance.
          </p>
        </div>
      </section>
    </div>
  );
};

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(227, 30, 36, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    maxWidth: '800px',
    margin: '0 auto',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  contactSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))',
    gap: '80px',
    alignItems: 'start',
  },
  contactInfo: {},
  contactH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#1A1F3A',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  contactText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#666',
    marginBottom: '48px',
  },
  contactMethods: {},
  contactMethod: {
    display: 'flex',
    gap: '16px',
    marginBottom: '32px',
    alignItems: 'flex-start',
  },
  contactMethodIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    background: '#F5F5F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contactMethodLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '700',
    marginBottom: '8px',
  },
  contactMethodValue: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    color: '#1A1F3A',
    fontWeight: '700',
    marginBottom: '6px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  contactMethodNote: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#999',
    fontStyle: 'italic',
  },
  formContainer: {
    background: '#F5F5F5',
    borderRadius: '20px',
    padding: '40px 32px',
    border: '3px solid #E5E5E5',
    boxSizing: 'border-box',
    width: '100%',
  },
  formH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '36px',
    color: '#1A1F3A',
    marginBottom: '32px',
    letterSpacing: '1px',
  },
  form: {},
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#333',
    fontWeight: '700',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '16px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    border: '2px solid #CCC',
    borderRadius: '8px',
    background: '#FFFFFF',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '16px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    border: '2px solid #CCC',
    borderRadius: '8px',
    background: '#FFFFFF',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '16px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    border: '2px solid #CCC',
    borderRadius: '8px',
    background: '#FFFFFF',
    transition: 'all 0.3s ease',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  submitBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
    boxShadow: '0 6px 20px rgba(227, 30, 36, 0.3)',
  },
  responseSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '80px 0',
    textAlign: 'center',
  },
  responseH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  responseText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    color: '#FFFFFF',
    maxWidth: '800px',
    margin: '0 auto',
    fontWeight: '600',
  },
};

export default ContactPage;
